import { type ReactNode, useMemo, useRef, useState } from "react";

import { useVirtualizer } from "@tanstack/react-virtual";

import { AdminAreaHierarchy } from "@/types/adminArea";

import { twMerge } from "tailwind-merge";

import SearchBox from "../ui/searchBox";
import { TreeNode } from "./TreeNode";

const TEST_ID_PREFIX = "searchbox-area-filter";

type Props = {
  className?: string;
  hierarchyData?: AdminAreaHierarchy[];
  selectedAreaIds: string[];
  setSelectedIds: (ids: string[]) => void;
  /** Optional content rendered right below the search box (e.g. a limit warning). */
  limitWarningSlot?: ReactNode;
};

const ROW_HEIGHT = 24;

export const AdminAreaTreeSelection = ({
  className,
  hierarchyData,
  selectedAreaIds,
  setSelectedIds,
  limitWarningSlot
}: Props) => {
  const { nodeMap, parentMap } = useMemo(() => {
    const nMap = new Map<string, AdminAreaHierarchy>();
    const pMap = new Map<string, AdminAreaHierarchy>();
    const build = (
      nodes: AdminAreaHierarchy[],
      parent?: AdminAreaHierarchy
    ) => {
      for (const node of nodes) {
        nMap.set(node.id, node);
        if (parent) {
          pMap.set(node.id, parent);
        }
        if (node.sub_areas) {
          build(node.sub_areas, node);
        }
      }
    };
    if (hierarchyData) {
      build(hierarchyData);
    }
    return { nodeMap: nMap, parentMap: pMap };
  }, [hierarchyData]);

  const selectedIds = useMemo(() => {
    if (selectedAreaIds.length === 0) {
      return new Set<string>();
    }

    const allSelected = new Set<string>();
    for (const id of selectedAreaIds) {
      const startNode = nodeMap.get(id);
      if (startNode) {
        const stack = [startNode];
        while (stack.length > 0) {
          const currentNode = stack.pop()!;
          allSelected.add(currentNode.id);
          if (currentNode.sub_areas) {
            stack.push(...currentNode.sub_areas);
          }
        }
      }
    }
    return allSelected;
  }, [selectedAreaIds, nodeMap]);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const data: AdminAreaHierarchy[] = useMemo(() => {
    if (!hierarchyData) return [];
    return hierarchyData;
  }, [hierarchyData]);

  // expand toggle manual
  const handleToggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // collect expanded Ids
  const collect = (nodes: AdminAreaHierarchy[], allIds: Set<string>) => {
    for (const n of nodes) {
      allIds.add(n.id);
      if (n.sub_areas) collect(n.sub_areas, allIds);
    }
  };

  // expand/collapse all
  const handleExpandAll = (expand: boolean) => {
    const allIds = new Set<string>();
    collect(data, allIds);
    setExpandedIds(expand ? allIds : new Set());
  };

  const flattenTreeIds = (nodes: AdminAreaHierarchy[]): string[] => {
    const result: string[] = [];

    const stack = [...nodes];
    while (stack.length) {
      const node = stack.pop()!;
      result.push(node.id);
      if (node.sub_areas) {
        stack.push(...node.sub_areas);
      }
    }

    return result;
  };

  const allNodeIds = useMemo(() => flattenTreeIds(data), [data]);

  const allExpanded = useMemo(() => {
    return allNodeIds.length > 0 && expandedIds.size === allNodeIds.length;
  }, [expandedIds, allNodeIds]);

  const handleCheck = (
    node: AdminAreaHierarchy,
    currentSelection: Set<string>
  ) => {
    // Remove any children of the newly selected node from the selection
    const stack = node.sub_areas ? [...node.sub_areas] : [];
    while (stack.length > 0) {
      const subNode = stack.pop()!;
      currentSelection.delete(subNode.id);
      if (subNode.sub_areas) {
        stack.push(...subNode.sub_areas);
      }
    }
    currentSelection.add(node.id);

    // "Roll up" selection to parent if all children are now selected
    let parent = parentMap.get(node.id);
    while (parent) {
      const allChildrenSelected = parent.sub_areas?.every((child) =>
        currentSelection.has(child.id)
      );

      if (allChildrenSelected) {
        parent.sub_areas?.forEach((child) => currentSelection.delete(child.id));
        currentSelection.add(parent.id);
        parent = parentMap.get(parent.id);
      } else {
        break;
      }
    }
  };

  const handleUncheck = (
    node: AdminAreaHierarchy,
    currentSelection: Set<string>
  ) => {
    // Find which top-level selected node is an ancestor of the un-checked node
    let ancestorInSelection: AdminAreaHierarchy | undefined;
    let current = node;
    while (parentMap.has(current.id)) {
      const parent = parentMap.get(current.id)!;
      if (currentSelection.has(parent.id)) {
        ancestorInSelection = parent;
        break;
      }
      current = parent;
    }

    // If an ancestor was selected (e.g. unchecking a child of a fully selected parent)
    if (ancestorInSelection) {
      currentSelection.delete(ancestorInSelection.id);
      // Add all siblings of the un-checked path back into the selection
      for (const sibling of ancestorInSelection.sub_areas ?? []) {
        if (sibling.id !== node.id) {
          // Need to check if the sibling is an ancestor of the node being unchecked
          let isAncestor = false;
          let p = parentMap.get(node.id);
          while (p) {
            if (p.id === sibling.id) {
              isAncestor = true;
              break;
            }
            p = parentMap.get(p.id);
          }

          if (!isAncestor) {
            currentSelection.add(sibling.id);
          }
        }
      }
      // Add intermediate nodes back
      let p = parentMap.get(node.id);
      while (p && p.id !== ancestorInSelection.id) {
        for (const sibling of p.sub_areas ?? []) {
          if (sibling.id !== node.id) {
            currentSelection.add(sibling.id);
          }
        }
        node = p;
        p = parentMap.get(p.id);
      }
    } else {
      // If the node itself is selected, remove it and all its descendants
      const stack = [node];
      while (stack.length > 0) {
        const currentNode = stack.pop()!;
        currentSelection.delete(currentNode.id);
        if (currentNode.sub_areas) {
          stack.push(...currentNode.sub_areas);
        }
      }
    }
  };

  // select logic
  const handleToggleSelect = (checked: boolean, node: AdminAreaHierarchy) => {
    const currentTopLevelSelected = new Set(selectedAreaIds);

    if (checked) {
      handleCheck(node, currentTopLevelSelected);
    } else {
      handleUncheck(node, currentTopLevelSelected);
    }

    setSelectedIds([...currentTopLevelSelected]);
  };

  // filter tree + auto-expand parent
  const { filteredData, autoExpandedIds } = useMemo(() => {
    if (!searchTerm.trim()) {
      return { filteredData: data, autoExpandedIds: expandedIds };
    }

    const lower = searchTerm.toLowerCase();
    const expanded = new Set(expandedIds);

    const filterRecursive = (
      nodes: AdminAreaHierarchy[]
    ): AdminAreaHierarchy[] => {
      const result: AdminAreaHierarchy[] = [];

      for (const n of nodes) {
        const selfMatch = n.name.toLowerCase().includes(lower);
        const childrenFiltered = n?.sub_areas?.length
          ? filterRecursive(n.sub_areas)
          : [];

        if (selfMatch) {
          // Parent matches. We need to check if children also match to expand.
          if (childrenFiltered.length > 0) {
            expanded.add(n.id);
            result.push({ ...n, sub_areas: childrenFiltered });
          } else {
            // Parent matches, but no children do. Show parent with all its original children.
            result.push({ ...n });
          }
        } else if (childrenFiltered.length > 0) {
          // Child matches → show parent and auto-expand it
          expanded.add(n.id);
          result.push({ ...n, sub_areas: childrenFiltered });
        }
      }
      return result;
    };

    const filtered = filterRecursive(data);
    return { filteredData: filtered, autoExpandedIds: expanded };
  }, [searchTerm, data, expandedIds]);

  const selectedCount = selectedAreaIds.length;

  const isIndeterminate = (node: AdminAreaHierarchy) => {
    const children = node.sub_areas ?? [];

    if (selectedIds.has(node.id)) {
      return false;
    }

    if (children.length === 0) {
      return false;
    }

    const hasSelectedChild = children.some((child) =>
      selectedIds.has(child.id)
    );
    const hasIndeterminateChild = children.some((child) =>
      isIndeterminate(child)
    );

    return hasSelectedChild || hasIndeterminateChild;
  };

  const parentRef = useRef<HTMLDivElement>(null);
  const visibleRows = useMemo(() => {
    const rows: { node: AdminAreaHierarchy; depth: number }[] = [];

    const walk = (nodes: AdminAreaHierarchy[], depth: number) => {
      for (const n of nodes) {
        rows.push({ node: n, depth });

        if (autoExpandedIds.has(n.id) && n.sub_areas) {
          walk(n.sub_areas, depth + 1);
        }
      }
    };

    walk(filteredData, 0);
    return rows;
  }, [filteredData, autoExpandedIds]);

  const rowVirtualizer = useVirtualizer({
    count: visibleRows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    measureElement: (el) => el.getBoundingClientRect().height
  });

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <SearchBox
        disabled={false}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeHolder="Search"
        className="max-w-xs py-3 my-3 text-[12px] bg-white"
        testId={TEST_ID_PREFIX}
      />

      {limitWarningSlot}

      <div className="flex items-center justify-between">
        <span aria-label={`Selected Area count: ${selectedCount}`}>
          Selected Area ({selectedCount})
        </span>
        <button
          type="button"
          className="text-primary-teal text-xs font-medium hover:underline"
          onClick={() => handleExpandAll(!allExpanded)}
        >
          {allExpanded ? "Collapse All" : "Expand All"}
        </button>
      </div>

      <div
        ref={parentRef}
        className={twMerge(
          "overflow-y-auto max-h-[calc(100vh-300px)] relative custom-scrollbar mt-4 mb-2",
          className
        )}
      >
        <div
          style={{
            height: rowVirtualizer.getTotalSize(),
            position: "relative"
          }}
        >
          {rowVirtualizer.getVirtualItems().map((v) => {
            const row = visibleRows[v.index];

            return (
              <div
                data-index={v.index} // for react-virtualizer to track
                key={row.node.id}
                ref={rowVirtualizer.measureElement}
                style={{
                  position: "absolute",
                  top: 0,
                  transform: `translateY(${v.start}px)`,
                  width: "100%"
                }}
              >
                <TreeNode
                  node={row.node}
                  depth={row.depth}
                  isExpanded={autoExpandedIds.has(row.node.id)}
                  isSelected={selectedIds.has(row.node.id)}
                  isIndeterminate={isIndeterminate(row.node)}
                  hasChildren={
                    row.node.sub_areas !== undefined &&
                    row.node.sub_areas.length > 0
                  }
                  onToggleExpand={handleToggleExpand}
                  onToggleSelect={handleToggleSelect}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
